<template>
  <BoxWithTitle
    collapsible
    noPadding
    openingIcon="mdi-chevron-down"
    closingIcon="mdi-chevron-up"
    class="noBoxShadow"
  >
    <template #header>
      <div class="titleStyle">
        {{ $t('massBalance.simplified.name') }}
      </div>
    </template>
    <template #content>
      <v-skeleton-loader
        v-if="loading"
        class="m-auto"
        type="table"
        transition="scale-transition"
      />
      <div class="contentStyle">
        <div class="contentStyleBtn">
          <v-btn
            class="siteTree-btn"
            color="secondary"
            @click="addMassBalance"
          >
            <v-icon
              color="#ffffff"
              left
            >
              mdi-plus
            </v-icon>
            {{ $t('administration.add') }}
          </v-btn>
        </div>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('laboratory.placeholder.labWoSearchBy')"
          :filterName="filterName"
        />
        <div
          v-if="massBalanceData.length > 0"
          class="contentStyleBody"
        >
          <div
            v-for=" (massBalance, index) in massBalanceData"
            :key="index"
          >
            <div
              class="contentStyleBodyEl"
            >
              <div class="rightContent">
                <div class="rightContentBorder" />
                <div class="d-flex align-center">
                  <v-icon
                    large
                    color="primary"
                  >
                    mdi-list-box-outline
                  </v-icon>
                </div>
                <div class="d-flex align-center rightContentText">
                  {{ massBalance.name }}
                </div>
              </div>
              <div class="leftContent">
                <v-menu offsetY>
                  <template #activator="{ on }">
                    <div class="d-flex align-center">
                      <v-btn
                        color="primary"
                        xSmall
                        fab
                        rounded
                        v-on="on"
                      >
                        <v-icon>
                          mdi-dots-vertical
                        </v-icon>
                      </v-btn>
                    </div>
                  </template>

                  <v-list>
                    <!-- Edit -->
                    <ListItem
                      icon="mdi-pencil"
                      :title="$t('massBalance.editMassBalance')"
                      @click="updateMassBalance(massBalance.id)"
                    />
                    <!-- Delete -->
                    <ListItem
                      icon="mdi-delete"
                      :title="$t('massBalance.delete')"
                      titleColor="#EF4135"
                      color="#EF4135"
                      @click="deleteMassBalanceData(massBalance.id)"
                    />
                  </v-list>
                </v-menu>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="title text-center"
        >
          {{ $t('massBalance.coolingTowerSection.emptyMassBalance') }}
        </div>
      </div>
    </template>
  </BoxWithTitle>
</template>

<script>
import BoxWithTitle from '@/components/shared/containers/VBoxWithTitle';
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';

import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ListItem from '@/components/shared/widgets/ListItem.vue';
import MassBalanceForm from '@/views/sites/massBalance/MassBalanceForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import { SIMPLIFIED } from '@/aquassist-shared/constants/massBalance';

export default {
    name: 'CoolingTowerComponent',
    components: {
        ListItem,
        BoxWithTitle,
        SearchWithFilters,
    },
    data() {
        return {
            loading: false,
            massBalanceData: [],
            filterName: 'coolingTower',
            queryParams: {},
        };
    },
    computed: {
        ...mapGetters([
            'site',
            'client',
            'team',
        ]),
    },

    async created() {
        await this.getMassBalanceData();
    },

    methods: {
        ...mapActions({
            resetMassBalanceData: 'resetMassBalanceData',
        }),
        async reload() {
            this.massBalanceData = [];
            await this.getMassBalanceData();
        },

        async addMassBalance() {
            this.$root.$emit('showModal', {
                showNextPrevious: true,
                title: this.$t('massBalance.coolingTowerSection.add'),
                component: Vue.component('MassBalanceForm', MassBalanceForm),
                payload: {
                    sample: {
                        site: this.site.name,
                        client: this.client.name,
                    },
                    massBalanceType: 'simplified',
                },
                controlledValidation: true,
                resetData: this.resetMassBalanceData,
                onConfirm: async requestPayload => {
                    await this.addMassBalanceData(requestPayload);
                    await this.resetMassBalanceData();
                },
                width: 1000,
            });
        },

        async updateMassBalance(massBalanceId) {
            const { data } = await this.$rq.getOneMassBalanceConfig(this.site.id, massBalanceId);
            this.$root.$emit('showModal', {
                showNextPrevious: true,
                title: this.$t('massBalance.coolingTowerSection.add'),
                component: Vue.component('MassBalanceForm', MassBalanceForm),
                payload: {
                    sample: {
                        site: this.site.name,
                        client: this.client.name,
                    },
                    massBalanceSelected: data,
                    massBalanceType: 'simplified',
                    isDisableModalHeader: false,
                },
                controlledValidation: true,
                onConfirm: async requestPayload => {
                    await this.editMassBalanceData(requestPayload, massBalanceId);
                    await this.resetMassBalanceData();
                },
                width: 1000,
            });
        },
        async addMassBalanceData(requestPayload) {
            const payload = this.preparePayload(requestPayload);
            await this.$rq.addAllMassBalanceConfig(this.site.id, payload);
            await this.reload();
        },
        async editMassBalanceData(requestPayload, massBalanceId) {
            const payload = this.preparePayload(requestPayload);
            await this.$rq.editOneMassBalanceConfig(this.site.id, massBalanceId, payload);
            await this.reload();
        },
        preparePayload(requestPayload) {
            const payload = {
                name: requestPayload.massBalanceStep?.name,
                type: 'simplified',
                reportUnit: requestPayload.massBalanceStep?.reportUnit,
                flowmeters: requestPayload.flowMeters?.data,
                indicatorType: requestPayload.massBalanceStep?.indicatorType,
                injectionControl: requestPayload.massBalanceStep?.injectionControl,
                flowmeterGeneralProps: requestPayload.flowMeters?.flowmeterGeneralProps,
            };
            if (requestPayload?.products?.data?.length > 0) {
                payload.products = requestPayload.products.data.map(p => ({
                    ...p,
                    ppm: Number(p.ppm),
                }));
            }
            return payload;
        },

        async getMassBalanceData() {
            this.loading = true;
            const { data } = await this.$rq.getAllMassBalanceConfig(this.site.id);
            this.massBalanceData = data.filter(p => (p.type === SIMPLIFIED));
            this.loading = false;
        },
        async deleteMassBalanceData(massBalanceId) {
            this.$root.$emit('showModal', {
                title: this.$t('massBalance.coolingTowerSection.removeMassBalance'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: { msg: this.$t('massBalance.coolingTowerSection.removeMassBalance') },
                onConfirm: () => this.removeMassBalance(massBalanceId),
                isForm: false,
            });
        },
        async removeMassBalance(massBalanceId) {
            await this.$rq.deleteOneMassBalanceConfig(this.site.id, massBalanceId);
            await this.reload();
        },
    },

};
</script>

  <style scoped lang="scss">
    @import "@/assets/styles/massBalanceStyle.scss";
  </style>
